import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { LoadingState } from '@/components/ui';
import type { PageId } from '@/features/navigation/pages';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { useSession } from './session-context';

/** Where /sign-in returns to: only for the same person whose session ended. */
export interface SignInRedirectState {
  from?: string;
  /** Staff id of the expired session; another account goes to its start page. */
  userId?: string;
}

/**
 * Requires a signed-in staff member with an active role. While the session is
 * being restored from the refresh cookie it shows a loader; otherwise it sends
 * the visitor to /sign-in (which also hosts the role picker).
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { session } = useSession();
  const location = useLocation();
  if (session.status === 'restoring')
    return <LoadingState label="Restoring your session" />;
  if (session.status !== 'authenticated' || !session.me.activeRole) {
    // A deliberate sign-out starts fresh: the next person lands on their own
    // role's start page, not on the page the previous user had open (RL-1040).
    // After an expiry the same person is returned to where they were.
    if (session.status === 'anonymous' && session.reason === 'signed_out')
      return <Navigate to="/sign-in" replace />;
    const state: SignInRedirectState = {
      from: location.pathname + location.search,
      userId:
        session.status === 'anonymous' ? session.previousUserId : undefined,
    };
    return <Navigate to="/sign-in" replace state={state} />;
  }
  return children ?? <Outlet />;
}

/** Capability gate for a page, using the active role's permissions from the API. */
export function RequirePermission({
  permission,
  pageId,
  children,
}: {
  permission: string;
  /** The page being opened, so "no access" can say whose page it is. */
  pageId?: PageId;
  children: ReactNode;
}) {
  const { hasPermission } = useSession();
  return hasPermission(permission) ? (
    children
  ) : (
    <ForbiddenPage pageId={pageId} />
  );
}
